<script setup lang="ts">
import { computed, ref } from 'vue';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const emit = defineEmits<{
    insert: [options: { rows: number; cols: number; caption: string }];
    close: [];
}>();

defineProps<{ open: boolean }>();

const rows = ref(3);
const cols = ref(3);
const caption = ref('');

// ── Grid preview ───────────────────────────────────────────────────────
const MAX_PREVIEW = 8;
const previewRows = computed(() => Math.min(rows.value, MAX_PREVIEW));
const previewCols = computed(() => Math.min(cols.value, MAX_PREVIEW));

function onInsert() {
    emit('insert', {
        rows: Math.max(1, rows.value),
        cols: Math.max(1, cols.value),
        caption: caption.value.trim(),
    });
    // Reset for next use
    rows.value = 3;
    cols.value = 3;
    caption.value = '';
}
</script>

<template>
    <Dialog :open="open" @update:open="(v) => !v && emit('close')">
        <DialogContent class="max-w-sm">
            <DialogHeader>
                <DialogTitle>Insérer un tableau</DialogTitle>
            </DialogHeader>

            <div class="space-y-4 py-2">
                <!-- Dimensions -->
                <div class="grid grid-cols-2 gap-4">
                    <div class="space-y-1.5">
                        <Label for="tbl-rows">Lignes</Label>
                        <Input
                            id="tbl-rows"
                            v-model.number="rows"
                            type="number"
                            min="1"
                            max="30"
                        />
                    </div>
                    <div class="space-y-1.5">
                        <Label for="tbl-cols">Colonnes</Label>
                        <Input
                            id="tbl-cols"
                            v-model.number="cols"
                            type="number"
                            min="1"
                            max="20"
                        />
                    </div>
                </div>

                <!-- Caption -->
                <div class="space-y-1.5">
                    <Label for="tbl-caption">Légende (optionnelle)</Label>
                    <Input
                        id="tbl-caption"
                        v-model="caption"
                        placeholder="Ex : Caractéristiques des patients inclus"
                    />
                </div>

                <!-- Mini grid preview -->
                <div class="flex flex-col items-center gap-0.5">
                    <p class="mb-1 text-xs text-muted-foreground">
                        Aperçu ({{ rows }}×{{ cols }})
                    </p>
                    <div
                        class="inline-grid gap-0.5"
                        :style="{
                            gridTemplateColumns: `repeat(${previewCols}, 1.5rem)`,
                        }"
                    >
                        <template v-for="r in previewRows" :key="r">
                            <div
                                v-for="c in previewCols"
                                :key="c"
                                class="h-5 w-6 rounded-sm border border-border"
                                :class="r === 1 ? 'bg-muted' : 'bg-background'"
                            />
                        </template>
                    </div>
                    <p
                        v-if="rows > MAX_PREVIEW || cols > MAX_PREVIEW"
                        class="mt-1 text-[10px] text-muted-foreground"
                    >
                        Aperçu tronqué à {{ MAX_PREVIEW }}
                    </p>
                </div>
            </div>

            <DialogFooter>
                <Button variant="outline" @click="emit('close')"
                    >Annuler</Button
                >
                <Button @click="onInsert">Insérer</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
